import { FC, RefObject, useEffect, useRef } from 'react'
import {
  ActionBar,
  Box,
  copyToClipboard,
  Icon,
  IconButton,
  Relative,
  Tooltip,
  TooltipController,
  useToast,
  VStack,
} from '@revolut-internal/ui-kit'
import { Link } from 'react-router-dom'

import {
  AssistantChatMessageFeedback,
  AssistantChatMessageRating,
  AuthorRole,
  Message,
} from 'lib/apiClient'
import { getDebugRouteV2 } from 'lib/router'
import { handleError } from 'lib/sentry'
import {
  CopyToClipboardErrorToast,
  CopyToClipboardSuccessToast,
} from 'components/CopyToClipboardToast'
import { extractTextFromHtml } from 'lib/utils/messages'
import { useIsVisibleOnScreen } from 'lib/hooks/useIsInView'
import { ChatFeedbackActions } from 'components/ChatFeedbackActions/ChatFeedbackActions'
import { ChatMessageActionItem } from 'components/ChatMessageActionItem/ChatMessageActionItem'

import { MessageBubble, MessageDate, TextMessage } from './components'
import { getMessageColorSchema } from './ChatMessage.utils'
import { ChatMessageWrapper } from './ChatMessage.styles'

interface ChatMessageProps {
  message: Message
  isDebugView?: boolean
  isSelected?: boolean
  onClick?: () => void
  onAssistantMessageVisible?: (id: string) => void
  containerRef: RefObject<HTMLElement> | null
  getChatMessageFeedback: (id: string) => AssistantChatMessageFeedback | null
  refetchChatMessageFeedback: (
    id: string,
    tempRating: AssistantChatMessageRating,
  ) => Promise<AssistantChatMessageFeedback | null>
  isFeedbackLoading: boolean
}

export const ChatMessage: FC<ChatMessageProps> = ({
  message,
  containerRef,
  isDebugView,
  isSelected,
  onClick,
  onAssistantMessageVisible,
  getChatMessageFeedback,
  refetchChatMessageFeedback,
  isFeedbackLoading,
}) => {
  const toast = useToast()

  const messageRef = useRef<HTMLElement | null>(null)

  const { isVisible, wasVisible } = useIsVisibleOnScreen(messageRef, containerRef)

  const chatMessageFeedback = getChatMessageFeedback(message.messageId)

  const colorSchema = getMessageColorSchema({
    messageType: message.authorRole,
    isDebugView,
  })
  const isUserMessage = message.authorRole === AuthorRole.Customer

  useEffect(() => {
    if (!messageRef.current || !onAssistantMessageVisible) {
      return
    }

    if (!wasVisible && isVisible && !isUserMessage) {
      onAssistantMessageVisible(message.messageId)
    }
  }, [isUserMessage, isVisible, message.messageId, onAssistantMessageVisible, wasVisible])

  const handleCopy = async () => {
    try {
      // Message text contains HTML entities that need to be decoded so that the user has clean text copied to the clipboard
      await copyToClipboard(extractTextFromHtml(message.text))
      toast.show(<CopyToClipboardSuccessToast />, 'short')
    } catch (e) {
      handleError(e)
      toast.show(<CopyToClipboardErrorToast />, 'short')
    }
  }

  return (
    <Relative>
      <ChatMessageWrapper
        isUserMessage={isUserMessage}
        ref={messageRef}
        data-testid={`message-bubble-${message.messageId}`}
      >
        <VStack maxWidth="80%" onClick={onClick}>
          <MessageBubble
            colorSchema={colorSchema}
            position={isUserMessage ? 'right' : 'left'}
            isInteractable={Boolean(onClick)}
            isSelected={isSelected}
          >
            <Box px="s-16" pt="s-8" pb="s-8" display="inline-table">
              <TextMessage text={message.text} colorSchema={colorSchema} />
              <MessageDate createdAt={message.created} colorSchema={colorSchema} />
            </Box>
          </MessageBubble>
        </VStack>
        <Box aria-label="Chat message actions" height="s-24">
          <ActionBar dir={isUserMessage ? 'rtl' : 'ltr'}>
            {!isUserMessage && !isFeedbackLoading && (
              <ChatFeedbackActions
                message={message}
                messageFeedback={chatMessageFeedback}
                refetchChatMessageFeedback={refetchChatMessageFeedback}
              />
            )}

            <TooltipController>
              {tooltip => (
                <>
                  <ChatMessageActionItem
                    iconOnly
                    aria-label="Copy"
                    onClick={handleCopy}
                    useIcon={
                      <IconButton
                        use={Box}
                        size={16}
                        useIcon={<Icon name="Copy" color="grey-tone-20" size={16} />}
                        useHoverIcon={<Icon name="Copy" color="accent" size={16} />}
                      >
                        Copy
                      </IconButton>
                    }
                    {...tooltip.getAnchorProps()}
                  >
                    Copy
                  </ChatMessageActionItem>
                  <Tooltip {...tooltip.getTargetProps()}>Copy</Tooltip>
                </>
              )}
            </TooltipController>

            {message.authorRole === AuthorRole.Customer && !isDebugView && (
              <TooltipController>
                {tooltip => (
                  <>
                    <ChatMessageActionItem
                      use={Link}
                      to={getDebugRouteV2(message.messageId)}
                      target="_blank"
                      iconOnly
                      aria-label="Debug"
                      useIcon={
                        <IconButton
                          use={Box}
                          size={16}
                          useIcon={<Icon name="Bug" color="grey-tone-20" size={16} />}
                          useHoverIcon={<Icon name="Bug" color="accent" size={16} />}
                        >
                          Debug
                        </IconButton>
                      }
                      {...tooltip.getAnchorProps()}
                    >
                      Debug
                    </ChatMessageActionItem>
                    <Tooltip {...tooltip.getTargetProps()}>Debug</Tooltip>
                  </>
                )}
              </TooltipController>
            )}
          </ActionBar>
        </Box>
      </ChatMessageWrapper>
    </Relative>
  )
}
